import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { sha256 } from "js-sha256";
import styles from "./AdminShared.module.css";
import AdminFieldForm from "./AdminFieldForm";
import { useAdminUI } from "./AdminUIContext";
import {
  buildBlankItem,
  getRequiredFieldsForItem,
  getUniqueTypes,
  itemsToArray,
  getResponseStatus,
  getResponseErrorMessage,
  toTitleCase,
  getItemTypeThemeKey,
  DEFAULT_CATALOG_TYPES,
  DEFAULT_CONTENT_TYPES,
  isTruthyFlag,
} from "./adminUtils";
import {
  saveManyCatalogItems,
  addDocToDB as addCatalogDoc,
} from "../../../storage/catalogDB";
import {
  saveManyContentItems,
  addDocToDB as addContentDoc,
} from "../../../storage/contentDB";
import {
  serializeDraftForSave,
  schemaSupportsDraft,
  canSaveDraftForType,
  detectDraftFieldStripped,
  publishConfirmCopy,
} from "../../../data/cmsDraft";
import {
  formatReleaseFullDate,
  isUpcomingRelease,
} from "../../../Hooks/catalogPresentation";
import {
  packConnectSplashIntoItem,
  isConnectSplashType,
} from "../../../data/connectSplashConfig";
import {
  packJoinInviteIntoItem,
  isJoinInviteType,
} from "../../../data/joinInviteConfig";
import {
  buildPreviewItemFromForm,
  openAdminPreview,
  previewItemLabel,
  removePreviewItem,
  upsertPreviewItem,
} from "../../../data/cmsPreview";
import {
  useIsInPreviewSet,
  usePreviewStores,
} from "../../../data/CmsPreviewContext";
import AdminPreviewActions from "./AdminPreviewActions";

const AdminCreateFlow = ({ section = "catalog" }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { toast, confirm } = useAdminUI();
  const user = useSelector((state) => state.auth.user);
  const catalogModel = useSelector((state) => state.catalogData.catalogModel);
  const contentModel = useSelector((state) => state.contentData.contentModel);
  const catalog = useSelector((state) => state.catalogData.catalog);
  const content = useSelector((state) => state.contentData.content);
  const catalogMetadata = useSelector(
    (state) => state.catalogData.catalogMetadata,
  );

  const model = section === "content" ? contentModel : catalogModel;
  const existing = itemsToArray(section === "content" ? content : catalog);
  const existingTypes = getUniqueTypes(existing);

  const defaultTypeHints =
    section === "content" ? DEFAULT_CONTENT_TYPES : DEFAULT_CATALOG_TYPES;

  const typeOptions = useMemo(() => {
    const set = new Set([...defaultTypeHints, ...existingTypes]);
    // Prefer canonical content/catalog order (same as list groups)
    const preferred = defaultTypeHints.filter((t) => set.has(t));
    const rest = Array.from(set)
      .filter((t) => !preferred.includes(t))
      .sort((a, b) => a.localeCompare(b));
    return [...preferred, ...rest];
  }, [existingTypes, defaultTypeHints]);

  const services = useMemo(() => {
    const avail = catalogMetadata?.availableServices || [];
    const inactive = catalogMetadata?.inactiveServices || [];
    return [...new Set([...avail, ...inactive].filter(Boolean))];
  }, [catalogMetadata]);

  const [forms, setForms] = useState([]);
  const [errors, setErrors] = useState([]);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [presetType, setPresetType] = useState(
    searchParams.get("type") || (section === "catalog" ? "song" : "noticeOne"),
  );
  const { previewItems } = usePreviewStores();
  const createPreviewId =
    forms[0]?.identifier || forms[0]?._localId || "";
  const includedInPreview = useIsInPreviewSet(section, createPreviewId);

  // Warn on tab close / refresh if the create draft was touched
  useEffect(() => {
    const onBeforeUnload = (e) => {
      if (!dirty) return;
      e.preventDefault();
      e.returnValue =
        "You have unsaved changes. Leave this page and discard them?";
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [dirty]);

  const makeForm = useCallback(
    (type, draft) => {
      if (draft) return { ...draft, _localId: `new-${Date.now()}-${Math.random()}` };
      const blank = buildBlankItem(model, { services, type, section });
      return {
        ...blank,
        _localId: `new-${Date.now()}-${Math.random()}`,
      };
    },
    [model, services, section],
  );

  useEffect(() => {
    if (!model) return;
    if (forms.length > 0) return;

    let draft = null;
    if (searchParams.get("from") === "duplicate") {
      try {
        const raw = sessionStorage.getItem("ms1-admin-create-draft");
        if (raw) {
          const parsed = JSON.parse(raw);
          if (parsed.section === section && parsed.draft) {
            draft = parsed.draft;
          }
          sessionStorage.removeItem("ms1-admin-create-draft");
        }
      } catch {
        /* ignore */
      }
    }

    setForms([makeForm(presetType, draft)]);
    setErrors([{}]);
  }, [model, makeForm, presetType, forms.length, searchParams, section]);

  const onChangeForm = (index, field, value) => {
    setDirty(true);
    setForms((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      return next;
    });
    setErrors((prev) => {
      const next = [...prev];
      if (next[index]?.[field]) {
        next[index] = { ...next[index] };
        delete next[index][field];
      }
      return next;
    });
  };

  const listPath = `/manage/${section}`;

  const confirmLeaveIfDirty = async () => {
    if (!dirty) return true;
    return confirm({
      title: "Leave without saving?",
      message:
        "You have unsaved create drafts. If you leave now, they will be lost.",
      confirmLabel: "Leave without saving",
      danger: true,
    });
  };

  const handleNavigateAway = async () => {
    if (!(await confirmLeaveIfDirty())) return;
    navigate(listPath);
  };

  const addForm = () => {
    setDirty(true);
    setForms((prev) => [...prev, makeForm(presetType)]);
    setErrors((prev) => [...prev, {}]);
  };

  const removeForm = async (index) => {
    if (forms.length <= 1) {
      toast.info("At least one form is required.");
      return;
    }
    const ok = await confirm({
      title: "Remove form?",
      message: "This draft will be discarded.",
      confirmLabel: "Remove",
      danger: true,
    });
    if (!ok) return;
    setForms((prev) => prev.filter((_, i) => i !== index));
    setErrors((prev) => prev.filter((_, i) => i !== index));
  };

  const validateAll = () => {
    const nextErrors = forms.map((form) => {
      const required = getRequiredFieldsForItem(model, form?.type, section);
      const e = {};
      required.forEach((field) => {
        const v = form[field];
        if (v == null || String(v).replace(/\s/g, "") === "") {
          e[field] = "Required";
        }
      });
      return e;
    });
    setErrors(nextErrors);
    return nextErrors.every((e) => Object.keys(e).length === 0);
  };

  const prepareForSave = (form) => {
    const date = new Date();
    const year = date.getFullYear();
    const output = { ...form };
    delete output._localId;
    delete output._id;
    delete output._storeKey;

    Object.keys(model || {}).forEach((key) => {
      if (key === "$timestamps") return;
      if (output[key] === undefined) output[key] = "";
    });

    if (!output.identifier) {
      const hashID = sha256(
        JSON.stringify((output.title || "untitled") + Date.now() + Math.random()),
      );
      output.identifier = year + "-" + hashID;
    }
    if (!output.createdAt) output.createdAt = date;
    output.updatedAt = date;

    if (output.sourceURLObj && typeof output.sourceURLObj === "object") {
      const cleaned = {};
      Object.entries(output.sourceURLObj).forEach(([k, v]) => {
        if (v) cleaned[k] = v;
      });
      // Keep empty strings for known services so player UI stays consistent
      services.forEach((s) => {
        if (!Object.hasOwn(cleaned, s)) cleaned[s] = output.sourceURLObj[s] || "";
      });
      output.sourceURLObj = cleaned;
    }

    return packJoinInviteIntoItem(packConnectSplashIntoItem(output));
  };

  const prepareCreateStub = (form) => {
    const date = new Date();
    const year = date.getFullYear();
    const stub = {};
    Object.keys(model || {}).forEach((key) => {
      if (key === "$timestamps") return;
      stub[key] = "";
    });
    stub.type = form.type || "";
    stub.title = form.title || "";
    stub.slug = form.slug || "";
    stub.active = "false";
    stub.identifier = form.identifier;
    if (!stub.identifier) {
      const hashID = sha256(
        JSON.stringify((stub.title || "untitled") + Date.now() + Math.random()),
      );
      stub.identifier = year + "-" + hashID;
    }
    stub.createdAt = form.createdAt || date;
    stub.updatedAt = date;
    const envelope = serializeDraftForSave(form, {
      section,
      type: form.type,
    });
    stub.draftPayload = envelope.draftPayload;
    delete stub.publishedAt;
    return stub;
  };

  const draftsSupported = schemaSupportsDraft(model);
  const allTypesAllowDraft = forms.every((form) =>
    canSaveDraftForType(form?.type),
  );
  const canSaveDraft = draftsSupported && allTypesAllowDraft;

  const handleSaveDraft = async () => {
    if (!model) {
      toast.error("Schema not loaded.");
      return;
    }
    if (!validateAll()) {
      toast.error("Please fill required fields on each form.");
      return;
    }
    if (!canSaveDraft) {
      toast.error(
        draftsSupported
          ? "Save draft is not available for this type yet. Use Publish to go live."
          : "Drafts need a backend deploy (draftPayload). Use Publish to go live.",
      );
      return;
    }

    setSaving(true);
    try {
      const stubs = forms.map(prepareCreateStub);
      if (stubs.length === 1) {
        const addFn = section === "content" ? addContentDoc : addCatalogDoc;
        const res = await addFn({ user, dataObj: stubs[0] });
        const status = getResponseStatus(res);
        if (status >= 200 && status < 300) {
          const savedDoc = res?.data;
          if (detectDraftFieldStripped(stubs[0].draftPayload, savedDoc)) {
            toast.error(
              "Drafts need a backend deploy (draftPayload). Use Publish to go live.",
            );
            return;
          }
          toast.success("Draft saved.");
          window.dispatchEvent(new CustomEvent("ms1-admin-refresh-data"));
          navigate(`/manage/${section}`);
        } else {
          toast.error(getResponseErrorMessage(res));
        }
        return;
      }

      const saveFn =
        section === "content" ? saveManyContentItems : saveManyCatalogItems;
      const res = await saveFn({ user, outputDataArray: stubs });
      const status = getResponseStatus(res);
      if (status >= 200 && status < 300) {
        toast.success(`${stubs.length} drafts saved.`);
        window.dispatchEvent(new CustomEvent("ms1-admin-refresh-data"));
        navigate(`/manage/${section}`);
      } else {
        toast.error(getResponseErrorMessage(res));
      }
    } catch (err) {
      toast.error(err.message || "Save draft failed");
    } finally {
      setSaving(false);
    }
  };

  const handlePreview = (splash) => {
    if (!validateAll()) {
      toast.error("Please fill required fields on each form.");
      return;
    }
    if (forms.length !== 1) {
      toast.error("Preview one item at a time.");
      return;
    }
    const result = openAdminPreview({
      values: forms[0],
      section,
      dirty: true,
      splash,
    });
    if (result.replaced) {
      toast.info(
        `Replaced the other ${previewItemLabel(result.replaced)} in the preview set.`,
      );
    }
    if (!result.ok) {
      toast.error("Could not store unsaved preview overlay.");
    }
  };

  const handleToggleInclude = () => {
    if (!validateAll()) {
      toast.error("Please fill required fields on each form.");
      return;
    }
    if (forms.length !== 1) {
      toast.error("Include one item at a time.");
      return;
    }
    const form = forms[0];
    const identifier = form.identifier || form._localId || "";
    if (includedInPreview) {
      removePreviewItem({ section, identifier });
      toast.success("Removed from the preview set.");
      return;
    }
    const result = upsertPreviewItem(
      buildPreviewItemFromForm({
        values: form,
        section,
        dirty: true,
      }),
    );
    if (!result.ok) {
      toast.error("Could not add this item to the preview set.");
      return;
    }
    if (result.replaced) {
      toast.info(
        `Replaced the other ${previewItemLabel(result.replaced)} in the preview set.`,
      );
    } else {
      toast.success(
        result.itemCount > 1
          ? `Added to the preview set (${result.itemCount} items).`
          : "Added to the preview set.",
      );
    }
  };

  const handlePublish = async () => {
    if (!model) {
      toast.error("Schema not loaded.");
      return;
    }
    if (!validateAll()) {
      toast.error("Please fill required fields on each form.");
      return;
    }

    const otherActiveSameType = forms.some((form) => {
      if (!isJoinInviteType(form.type) && !isConnectSplashType(form.type)) {
        return false;
      }
      if (!isTruthyFlag(form.active)) return false;
      return existing.some(
        (i) =>
          String(i.type || "")
            .trim()
            .toLowerCase() ===
            String(form.type || "")
              .trim()
              .toLowerCase() && isTruthyFlag(i.active),
      );
    });

    const ok = await confirm(
      publishConfirmCopy({
        visitorsWillSeeIt: forms.some((form) => isTruthyFlag(form.active)),
        otherActiveSameType,
        mode: "create",
      }),
    );
    if (!ok) return;

    setSaving(true);
    try {
      const outputDataArray = forms.map((form) => {
        const packed = prepareForSave(form);
        packed.draftPayload = "";
        packed.publishedAt = new Date();
        return packed;
      });
      const saveFn =
        section === "content" ? saveManyContentItems : saveManyCatalogItems;
      const res = await saveFn({ user, outputDataArray });
      const status = getResponseStatus(res);
      if (status >= 200 && status < 300) {
        toast.success(
          outputDataArray.length === 1
            ? "Item published."
            : `${outputDataArray.length} items published.`,
        );
        window.dispatchEvent(new CustomEvent("ms1-admin-refresh-data"));
        navigate(`/manage/${section}`);
      } else {
        toast.error(getResponseErrorMessage(res));
      }
    } catch (err) {
      toast.error(err.message || "Create failed");
    } finally {
      setSaving(false);
    }
  };

  const label = section === "content" ? "Content" : "Catalog";
  const saveLabel = saving
    ? "Saving…"
    : `Save draft${forms.length > 1 ? ` (${forms.length})` : ""}`;
  const publishLabel = saving
    ? "Publishing…"
    : `Publish${forms.length > 1 ? ` ${forms.length}` : ""}`;
  const saveButtonClass = dirty
    ? `${styles["btn-primary"]} ${styles["btn-save-needed"]}`
    : styles["btn-primary"];

  if (!model) {
    return (
      <div className={styles["loading-block"]}>Loading form schema…</div>
    );
  }

  return (
    <div className={dirty ? styles["page-has-unsaved"] : undefined}>
      <div className={`${styles["page-header"]} ${styles["page-header-stacked"]}`}>
        <div className={styles["page-header-content"]}>
          <h1 className={styles["page-title"]}>
            {dirty && (
              <span
                className={styles["dirty-dot"]}
                title="Unsaved changes"
                aria-hidden="true"
              />
            )}
            New {label} item
          </h1>
          <p className={styles["page-subtitle"]}>
            <span>Required fields are marked.</span>
            {dirty ? (
              <span className={styles["unsaved-inline-label"]}>
                Unsaved draft
              </span>
            ) : null}
          </p>
        </div>
        <div className={styles["page-toolbar"]}>
          <div className={styles["action-group"]} role="group" aria-label="Navigate">
            <button
              type="button"
              className={styles["btn-ghost"]}
              onClick={handleNavigateAway}
            >
              Cancel
            </button>
          </div>
          {forms.length === 1 && (
            <AdminPreviewActions
              type={forms[0]?.type}
              onPreview={handlePreview}
              disabled={saving}
              included={includedInPreview}
              onToggleInclude={handleToggleInclude}
              includeCount={previewItems.length}
            />
          )}
          <div className={styles["action-group"]} role="group" aria-label="Save">
            <button
              type="button"
              className={saveButtonClass}
              onClick={handleSaveDraft}
              disabled={saving || !canSaveDraft}
              title={
                canSaveDraft
                  ? "Save without changing the live site"
                  : "Save draft is not available"
              }
            >
              {saveLabel}
            </button>
            <button
              type="button"
              className={styles["btn-secondary"]}
              onClick={handlePublish}
              disabled={saving}
            >
              {publishLabel}
            </button>
          </div>
        </div>
      </div>

      <div className={styles.panel}>
        <div className={styles["batch-create-panel"]}>
          <div className={styles["batch-create-copy"]}>
            <p className={styles["batch-create-eyebrow"]}>
              Optional — batch create
            </p>
            <h2 className={styles["batch-create-title"]}>
              Adding more than one item?
            </h2>
            <p className={styles["field-hint"]}>
              This type only applies when you click{" "}
              <strong>+ Add another form</strong> below. It does{" "}
              <strong>not</strong> change the item you are already editing —
              each form has its own <strong>Type</strong> dropdown.
            </p>
          </div>
          <div className={styles["create-count-row"]}>
            <div className={styles.field}>
              <label htmlFor="preset-type">
                Type for the next form you add
              </label>
              <select
                id="preset-type"
                className={styles["select-input"]}
                value={presetType}
                onChange={(e) => setPresetType(e.target.value)}
              >
                {typeOptions.map((t) => (
                  <option key={t} value={t}>
                    {toTitleCase(t)}
                  </option>
                ))}
              </select>
            </div>
            <button
              type="button"
              className={styles["btn-secondary"]}
              onClick={addForm}
            >
              + Add another form
            </button>
          </div>
        </div>
      </div>

      {forms.map((form, index) => {
        const typeKey = getItemTypeThemeKey(form.type);
        const typeSurfaceClass = typeKey
          ? `${styles["item-type-surface"]} ${styles[`item-type-${typeKey}`]}`
          : "";
        const formUpcoming = isUpcomingRelease(form);
        const formDrop = formUpcoming ? formatReleaseFullDate(form) : "";
        return (
          <div
            key={form._localId}
            className={`${styles["form-card"]} ${typeSurfaceClass}`.trim()}
          >
            <div className={styles["form-card-header"]}>
              <div className={styles["form-card-heading"]}>
                <h3>
                  Item {index + 1}
                  {form.title ? ` — ${form.title}` : ""}
                </h3>
                {typeKey ? (
                  <span
                    className={`${styles["badge-item-type"]} ${styles[`item-type-${typeKey}`]}`}
                  >
                    {toTitleCase(typeKey)}
                  </span>
                ) : null}
                {formUpcoming ? (
                  <span
                    className={`${styles.badge} ${styles["badge-upcoming"]}`}
                    title={formDrop ? `Drops ${formDrop}` : "Upcoming release"}
                  >
                    Upcoming
                    {formDrop ? ` · ${formDrop}` : ""}
                  </span>
                ) : null}
              </div>
              {forms.length > 1 && (
                <button
                  type="button"
                  className={styles["btn-ghost"]}
                  onClick={() => removeForm(index)}
                >
                  Remove
                </button>
              )}
            </div>
            <AdminFieldForm
              model={model}
              values={form}
              onChange={(field, value) => onChangeForm(index, field, value)}
              errors={errors[index] || {}}
              services={services}
              section={section}
              showSystem={false}
              mode="create"
              typeOptions={typeOptions}
            />
          </div>
        );
      })}

      <div className={styles["form-footer"]} style={{ borderTop: "none" }}>
        <button type="button" className={styles["btn-secondary"]} onClick={addForm}>
          + Add another form
          {presetType ? ` (${toTitleCase(presetType)})` : ""}
        </button>
        <div
          className={`${styles["form-footer-actions"]} ${styles["action-group"]}`}
          role="group"
          aria-label="Save"
        >
          <button
            type="button"
            className={saveButtonClass}
            onClick={handleSaveDraft}
            disabled={saving || !canSaveDraft}
          >
            {saveLabel}
          </button>
          <button
            type="button"
            className={styles["btn-secondary"]}
            onClick={handlePublish}
            disabled={saving}
          >
            {publishLabel}
          </button>
        </div>
      </div>

      {dirty && (
        <div
          className={styles["unsaved-bar"]}
          role="status"
          aria-live="polite"
          aria-atomic="true"
        >
          <div className={styles["unsaved-bar-inner"]}>
            <p className={styles["unsaved-bar-text"]}>
              <span className={styles["unsaved-bar-dot"]} aria-hidden="true" />
              Unsaved create draft
            </p>
            <div className={styles["unsaved-bar-actions"]}>
              <div className={styles["action-group"]} role="group" aria-label="Unsaved changes">
                <button
                  type="button"
                  className={styles["btn-ghost"]}
                  onClick={handleNavigateAway}
                  disabled={saving}
                  title="Leave without saving"
                >
                  Leave
                </button>
                <button
                  type="button"
                  className={saveButtonClass}
                  onClick={handleSaveDraft}
                  disabled={saving || !canSaveDraft}
                >
                  {saveLabel}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminCreateFlow;
